import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Sun, Moon, Bell, Command } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

export default function Header() {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data);
      } catch (_) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const go = (url) => { setQ(""); setResults(null); navigate(url); };

  return (
    <header
      data-testid="app-header"
      className="h-14 sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl px-4 md:px-6 flex items-center gap-3"
    >
      <div className="flex-1 max-w-2xl relative">
        <div className="flex items-center gap-2 border border-border rounded-md bg-card px-3 h-9 focus-within:ring-2 focus-within:ring-primary transition-colors">
          <Search size={15} className="text-muted-foreground" strokeWidth={1.75} />
          <Input
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients, projects, tasks..."
            className="h-9 border-0 shadow-none focus-visible:ring-0 px-0 bg-transparent"
          />
          <kbd className="hidden md:inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5">
            <Command size={10} />K
          </kbd>
        </div>
        {results && (
          <div className="absolute top-11 left-0 right-0 rounded-md border border-border bg-popover shadow-xl overflow-hidden z-50">
            {["clients", "projects", "tasks", "users"].map((k) => (
              (results[k] || []).length > 0 && (
                <div key={k} className="border-b border-border last:border-0">
                  <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/50">{k}</div>
                  {results[k].map((it) => (
                    <button
                      key={it.id}
                      data-testid={`search-result-${k}-${it.id}`}
                      onClick={() => {
                        if (k === "clients") go(`/clients/${it.id}`);
                        else if (k === "projects") go(`/projects`);
                        else if (k === "tasks") go(`/tasks`);
                        else go(`/team`);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                    >
                      {it.company_name || it.name}
                    </button>
                  ))}
                </div>
              )
            ))}
            {!(results.clients?.length || results.projects?.length || results.tasks?.length || results.users?.length) && (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">No results for "{q}"</div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          data-testid="theme-toggle-btn"
          onClick={toggle}
          className="w-9 h-9 rounded-md border border-border hover:bg-muted transition-colors duration-150 flex items-center justify-center"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="notifications-btn"
              className="relative w-9 h-9 rounded-md border border-border hover:bg-muted transition-colors duration-150 flex items-center justify-center"
              aria-label="Notifications"
            >
              <Bell size={15} />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-mono font-bold flex items-center justify-center">{unread}</span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <span className="font-display font-bold">Notifications</span>
              {unread > 0 && (
                <button
                  data-testid="mark-all-read-btn"
                  onClick={async () => { await api.post("/notifications/read-all"); const r = await api.get("/notifications"); setNotifs(r.data); }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >Mark all read</button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifs.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">You're all caught up.</div>
              ) : notifs.map((n) => (
                <div key={n.id} className={`px-3 py-2 border-b border-border last:border-0 ${!n.read ? "bg-accent/40" : ""}`}>
                  <div className="text-sm font-semibold">{n.title}</div>
                  <div className="text-xs text-muted-foreground">{n.message}</div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {user && (
          <Link to="/team" data-testid="header-user-link" className="hidden md:flex items-center gap-2 pl-2 border-l border-border ml-1">
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-mono text-xs font-semibold">
              {(user.name || "?").split(" ").map((s) => s[0]).slice(0,2).join("")}
            </div>
            <div className="text-sm leading-tight">
              <div className="font-semibold">{user.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{user.role?.replace("_"," ")}</div>
            </div>
          </Link>
        )}
      </div>
    </header>
  );
}
